import React, { useEffect, useState } from 'react';
import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon, CheckIcon, ChevronUpDownIcon, PlusIcon, CogIcon } from '@heroicons/react/24/outline';
import { Listbox, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import NafCodeSelector, { NafCode } from './NafCodeSelector';
import NafListSelectionModal from '../nafLists/NafListSelectionModal';
import ProspectListSelector from '../prospectLists/ProspectListSelector';
import { ProspectionViewType, ProspectionViewValues } from '../../store/slices/nouveauxAdherentsSlice';

import { getFirstDayOfPreviousMonth, formatDateToYYYYMMDD } from '../../utils/dateUtils';
import MultiInput from '../common/MultiInput';

// Import departements data
import { departements } from '../../data/departements';

// Categories of companies for filtering
// const categoriesEntreprise = [
//   { code: '', label: 'Toutes catégories' },
//   { code: 'PME', label: 'Petite ou Moyenne Entreprise (PME)' },
//   { code: 'ETI', label: 'Entreprise de Taille Intermédiaire (ETI)' },
//   { code: 'GE', label: 'Grande Entreprise (GE)' }
// ];

// Tranches effectifs mapping for filters
// const tranchesEffectifs = [
//   { code: '', label: 'Tous effectifs' },
//   { code: '00', label: 'Pas d\'effectif' },
//   { code: '01', label: '1 ou 2 salariés' },
//   { code: '02', label: '3 à 5 salariés' },
//   { code: '03', label: '6 à 9 salariés' },
//   { code: '11', label: '10 à 19 salariés' },
//   { code: '12', label: '20 à 49 salariés' },
//   { code: '21', label: '50 à 99 salariés' },
//   { code: '22', label: '100 à 199 salariés' },
//   { code: '31', label: '200 à 249 salariés' },
//   { code: '32', label: '250 à 499 salariés' },
//   { code: '41', label: '500 à 999 salariés' },
//   { code: '42', label: '1 000 à 1 999 salariés' },
//   { code: '51', label: '2 000 à 4 999 salariés' },
//   { code: '52', label: '5 000 à 9 999 salariés' },
//   { code: '53', label: '10 000 salariés et plus' }
// ];

// TODO defines exact filtering for each view

interface FilterFormValues {
  dateFrom: string;
  dateTo: string;
  nafCode: string;
  nafCodes: string[];
  postalCode: string;
  postalCodes: string[];
  city: string;
  departement: string;
  excludeMicroEntreprises: boolean;
  excludedProspectListIds: number[];
  excludeProspectsMode: 'siret' | 'siren';
}

interface EnhancedFilterFormProps {
  initialValues: FilterFormValues;
  onSubmit: (values: FilterFormValues) => void;
  isLoading: boolean;
  currentView: ProspectionViewType;
  nafCodes: NafCode[];
}

const EnhancedFilterForm: React.FC<EnhancedFilterFormProps> = ({
  initialValues,
  onSubmit,
  isLoading,
  currentView,
  nafCodes,
}) => {
  const [values, setValues] = useState<FilterFormValues>(initialValues);
  const [deptSearchQuery, setDeptSearchQuery] = useState('');
  const [isNafListModalOpen, setIsNafListModalOpen] = useState(false);

  // Set default date on first render
  useEffect(() => {
    if (!values.dateFrom) {
      const defaultFromDate = formatDateToYYYYMMDD(new Date(getFirstDayOfPreviousMonth()));
      setValues(prev => ({ ...prev, dateFrom: defaultFromDate }));
    }
  }, [values.dateFrom]);

  useEffect(() => {
    const updatedValues = {
      ...initialValues,
      nafCodes: initialValues.nafCodes || (initialValues.nafCode ? [initialValues.nafCode] : []),
      postalCodes: initialValues.postalCodes || [],
      excludedProspectListIds: initialValues.excludedProspectListIds || [],
      excludeProspectsMode: initialValues.excludeProspectsMode || 'siret',
    };
    setValues(updatedValues);
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  // Reset the form to default values
  const handleReset = () => {
    const defaultFromDate = formatDateToYYYYMMDD(new Date(getFirstDayOfPreviousMonth()));
    const defaultValues = {
      dateFrom: defaultFromDate,
      dateTo: '',
      nafCode: '',
      nafCodes: [],
      postalCode: '',
      postalCodes: [],
      city: '',
      departement: '66', // Default to Loire-Atlantique
      excludeMicroEntreprises: false,
      excludedProspectListIds: [],
      excludeProspectsMode: 'siret' as 'siret' | 'siren',
    };
    
    setValues(defaultValues);
    onSubmit(defaultValues); // Also submit the form with default values
  };

  // Filter départements based on search query
  const filteredDepartements = departements.filter(dept => {
    if (!deptSearchQuery) return true;
    
    const searchLower = deptSearchQuery.toLowerCase();
    return (
      dept.code.toLowerCase().includes(searchLower) ||
      dept.nom.toLowerCase().includes(searchLower)
    );
  });

  // Find the selected département object
  const selectedDept = departements.find(dept => dept.code === values.departement) || departements[0];

  const handleNafListSelection = (selectedNafCodes: string[]) => {
    // Add the selected NAF codes to the existing ones (avoiding duplicates)
    setValues(prev => {
      const existingCodes = prev.nafCodes || [];
      const newCodes = [...existingCodes];
      
      selectedNafCodes.forEach(code => {
        if (!newCodes.includes(code)) {
          newCodes.push(code);
        }
      });
      
      return {
        ...prev,
        nafCodes: newCodes,
        nafCode: newCodes.length > 0 ? newCodes[0] : '' // Keep single nafCode in sync for backward compatibility
      };
    });
  };

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range Fields - Show in all views except NON_CLIENT_FROM_CLIENT_COMPANIES */}
          {currentView !== ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES && (
            <>
              <div>
                <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700">
                  Date de création (début)
                </label>
                <input
                  type="date"
                  id="dateFrom"
                  name="dateFrom"
                  value={values.dateFrom}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  disabled={isLoading}
                />
              </div>
              
            </>
          )}
          
          {/* When the view changes to NON_CLIENT_FROM_CLIENT_COMPANIES, reset date values if needed */}
          {currentView === ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES && values.dateFrom && (
            <input type="hidden" name="dateFrom" value="" />
          )}
          
          {/* Department Field - Show in all views */}
          <div>
            <label htmlFor="departement" className="block text-sm font-medium text-gray-700">
              Département
            </label>
            <div className="relative mt-1">
              <Listbox 
                value={values.departement} 
                onChange={(code) => {
                  setValues(prev => ({ ...prev, departement: code }));
                }}
                disabled={isLoading}
              >
                {({ open }) => (
                  <>
                    <Listbox.Button 
                      className={`relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                    >
                      <span className="block truncate">
                        {selectedDept ? `${selectedDept.code} - ${selectedDept.nom}` : 'Sélectionner un département'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {/* Search input */}
                        <div className="sticky top-0 z-10 bg-white px-2 py-1.5">
                          <div className="relative">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="text"
                              className="block w-full rounded-md border-0 py-1.5 pl-10 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              placeholder="Rechercher un département..."
                              value={deptSearchQuery}
                              onChange={(e) => setDeptSearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()} // Prevent closing the dropdown
                              disabled={isLoading}
                            />
                            {deptSearchQuery && (
                              <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center pr-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeptSearchQuery('');
                                }}
                                disabled={isLoading}
                              >
                                <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-500" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {filteredDepartements.length > 0 ? (
                          filteredDepartements.map((dept) => (
                            <Listbox.Option
                              key={dept.code}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                                }`
                              }
                              value={dept.code}
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {dept.code} - {dept.nom}
                                  </span>
                                  {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))
                        ) : (
                          <div className="py-2 px-4 text-sm text-gray-500">
                            Aucun département trouvé pour cette recherche
                          </div>
                        )}
                      </Listbox.Options>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
          </div>
          
          {/* Postal Codes Field */}
          <div>
            <MultiInput
              label="Codes Postaux"
              values={values.postalCodes}
              onChange={(newPostalCodes) => {
                setValues(prev => ({ 
                  ...prev, 
                  postalCodes: newPostalCodes
                }));
              }}
              placeholder="Ex: 75001, 44000..."
              disabled={isLoading}
              helpText="Entrez un code postal puis cliquez sur ajouter"
              pattern="^\d{1,5}$"
              patternErrorMessage="Le code postal doit contenir uniquement des chiffres (5 max)"
            />
          </div>
          
          {/* NAF Code Management Section */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Liste de codes NAF
                </label>
              </div>

              {/* Action buttons row */}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setIsNafListModalOpen(true)}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Ajouter depuis une liste prédéfinie
                </button>
                
                <Link
                  to="/listes-naf"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <CogIcon className="h-4 w-4 mr-2" />
                  Gérer les listes NAF
                </Link>
              </div>

              {/* Individual NAF code selector */}
              <div>
                <NafCodeSelector 
                  nafCodes={nafCodes}
                  selectedNafCodes={values.nafCodes}
                  onChange={(codes) => {
                    setValues(prev => ({ 
                      ...prev, 
                      nafCodes: codes,
                      nafCode: codes.length > 0 ? codes[0] : '' // Keep single nafCode in sync for backward compatibility
                    }));
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Prospect lists exclusion selector */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            <ProspectListSelector
              disabled={isLoading}
              selectedListIds={values.excludedProspectListIds}
              onSelectedListIdsChange={(nextListIds) => {
                setValues((prev) => ({
                  ...prev,
                  excludedProspectListIds: nextListIds,
                }));
              }}
              excludeProspectsMode={values.excludeProspectsMode}
              onExcludeProspectsModeChange={(mode) => {
                setValues((prev) => ({
                  ...prev,
                  excludeProspectsMode: mode,
                }));
              }}
            />
          </div>

          {/* Add excludeMicroEntreprises checkbox after NAF field */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3 mt-2">
            <div className="flex items-center">
              <input
                id="excludeMicroEntreprises"
                name="excludeMicroEntreprises"
                type="checkbox"
                checked={values.excludeMicroEntreprises}
                onChange={(e) => setValues(prev => ({
                  ...prev,
                  excludeMicroEntreprises: e.target.checked
                }))}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                disabled={isLoading}
              />
              <label htmlFor="excludeMicroEntreprises" className="ml-2 block text-sm text-gray-900">
                Exclure les micro-entreprises
              </label>
            </div>
          </div>
        </div>
        
        {/* Submit and Reset Buttons */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 justify-end">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            Réinitialiser
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Chargement...
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="-ml-1 mr-2 h-4 w-4" />
                Rechercher
              </>
            )}
          </button>
        </div>
      </form>

      {/* NAF List Selection Modal */}
      <NafListSelectionModal
        isOpen={isNafListModalOpen}
        onClose={() => setIsNafListModalOpen(false)}
        onSelectLists={handleNafListSelection}
        disabled={isLoading}
      />
    </div>
  );
};

export default EnhancedFilterForm; 